// src/lib/download.js
//
// Blob downloads (invoice PDFs).
//
//   * the file name comes from the server's Content-Disposition header, with a
//     caller-supplied fallback;
//   * an error body requested as a blob is parsed back into JSON so
//     readApiError/apiErrorCode can read it -- raw bytes or JSON are never shown.

function stripQuotes(value) {
  const trimmed = String(value || "").trim();
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) return trimmed.slice(1, -1);
  return trimmed;
}

function safeName(name) {
  // Only a plain file name, never a path.
  const cleaned = Array.from(String(name || ""))
    .filter((ch) => ch.charCodeAt(0) >= 32)
    .join("")
    .replace(/[\\/]/g, "_")
    .trim();
  return cleaned || "";
}

export function filenameFromContentDisposition(header, fallback = "download") {
  const value = String(header || "");
  const star = value.match(/filename\*\s*=\s*([^;]+)/i);
  if (star) {
    const raw = stripQuotes(star[1]);
    const encoded = raw.includes("''") ? raw.slice(raw.indexOf("''") + 2) : raw;
    try {
      const decoded = safeName(decodeURIComponent(encoded));
      if (decoded) return decoded;
    } catch {
      // fall through to the plain parameter
    }
  }
  const plain = value.match(/filename\s*=\s*("[^"]*"|[^;]+)/i);
  if (plain) {
    const name = safeName(stripQuotes(plain[1]));
    if (name) return name;
  }
  return safeName(fallback) || "download";
}

export function readBlobText(blob) {
  if (!blob) return Promise.resolve("");
  if (typeof blob.text === "function") return blob.text();
  return new Promise((resolve) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => resolve("");
      reader.readAsText(blob);
    } catch {
      resolve("");
    }
  });
}

/** Turns an axios error whose `response.data` is a Blob into one with parsed JSON data. */
export async function normalizeBlobError(error) {
  const data = error?.response?.data;
  if (typeof Blob === "undefined" || !(data instanceof Blob)) return error;
  const text = await readBlobText(data);
  let parsed = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = null;
  }
  // Non-JSON bodies are dropped rather than shown.
  error.response = { ...error.response, data: parsed && typeof parsed === "object" ? parsed : null };
  return error;
}

export function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename || "download";
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    window.URL.revokeObjectURL(url);
  }
}
